import { CustomerStatus } from 'src/models/enums/CustomerStatus.enum';
import { BankStatus } from 'src/models/enums/BankStatus.enum';

export const userRoles = {
  OWNER: 'owner',
  ADMIN: 'admin',
  FACTORY_MANAGER: 'factory_manager',
  EXECUTIVE: 'executive',
};

export const rolePriorities: Record<string, number> = {
  owner: 1,
  admin: 2,
  'factory manager': 3,
  executive: 4,
};

export const regularExpressions = {
  PHONE_REGEX: /^\+(\d{1,4})\s(.+)$/,
  EMAIL_REGEX:
    /^[a-zA-Z0-9]+([._-]?[a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-]?[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/,
  NAME_REGEX: /[^a-zA-Z\s'-]/g,
  WHITESPACE_REGEX: /\s/g,
  NUMBER_ONLY_REGEX: /[^0-9.]/g,
  URL_REGEX: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
};

export const help = {
  REQUIRED: 'is required',
  VALUE_REQUIRED: 'Value is required',
  FIELD_REQUIRED: 'Field is required',
  PRODUCT_REQUIRED: 'Product is required',

  PASSWORD_MISMATCH: 'Passwords do not match',
  PASSWORD_MIN: 'Password must be at least 8 characters',
  PASSWORD_LOWERCASE: 'Password must contain at least one lowercase letter',
  PASSWORD_UPPERCASE: 'Password must contain at least one uppercase letter',
  PASSWORD_INTEGER: 'Password must contain at least one number',
  PASSWORD_SPECIAL_CHARACTER:
    'Password must contain at least one special character',
  PASSWORD_RESET_SIMILAR: 'New password cannot be the same as old password',

  INVALID_PHONE_NUMBER: 'Invalid phone number',
  INVALID_EMAIL_ADDRESS: 'Invalid email address',
  INVALID_URL: 'Invalid URL format',
  INVALID_FORMAT: 'Invalid format',
  TEXT_ONLY: 'Text only allowed',

  PERCENTAGE_OR_DEFAULT_REQUIRED:
    'Either percentage or default value is required',
  EITHER_PERCENTAGE_OR_DEFAULT_ONLY:
    'Only one of percentage or default value can be provided',
  CANNOT_EXCEED_100: 'Percentage cannot exceed 100',
  MIN_VALUE_REQUIRED: 'Value must be greater than 0',
  PRODUCED_QUANTITY_MIN: 'Produced Quantity must be at least 1',
  DAMAGED_QUANTITY_MIN: 'Damaged Quantity must be at least 0',
  DAMAGED_QUANTITY_EXCEEDS_PRODUCED:
    'Damaged Quantity cannot exceed Produced Quantity',
  DATE_CANNOT_BE_FUTURE: 'Production Date cannot be in the future',
  PAYMENT_DATE_CANNOT_BE_FUTURE: 'Future date not allowed',
  QUANTITY_REQUIRED: 'Quantity is required',
  QUANTITY_MIN: 'Quantity must be greater than 0',
  RAW_MATERIAL_REQUIRED: 'Raw Material is required',

  SCHEDULED_DATE_REQUIRED: 'Scheduled date is required',
  SCHEDULED_DATE_PAST: 'Scheduled delivery date cannot be in the past',
  ACTUAL_DELIVERY_DATE_REQUIRED: 'Actual delivery date is required',
  ACTUAL_DELIVERY_DATE_FUTURE: 'Actual delivery date cannot be in the future',
  CHEQUE_REALIZED_DATE_REQUIRED: 'Realized date is required',
  CHEQUE_REALIZED_DATE_FUTURE: 'Realized date cannot be in the future',

  SHORT_VALUE: 'Value is too short',

  CANNOT_BE_EMPTY: 'cannot be empty',
  MUST_BE_A_NUMBER: 'must be a number',
  CHECK_YOUR_EMAIL: 'Summary Sent to an email. Please check your email!',
  FILE_FORMAT_UNSUPPORTED: 'File format is unsupported',
  FILE_SIZE_BIG: 'File is too large',
  MUST_BE_DIGITS: 'Must be only digits',
  LOGIN_FAILED: 'Login failed. Please check your credentials.',
};

export const errorMessages = {
  ACCESS_DENIED: 'Access denied',
  SESSION_EXPIRED: 'Session expired',
  SESSION_REFRESH_FAILED: 'Session refresh failed',
  INTERNAL_SERVER_ERROR: 'Internal Server Error',
  SOMETHING_WENT_WRONG: 'Something went wrong. Please try again later.',

  COMPANY_EXISTS: 'Company already exists',
  REG_NO_EXISTS: 'Registration number already exists',
  EMAIL_ALREADY_VERIFIED: 'Email already verified',

  UNAUTHORIZED_TO_EDIT_OWNER:
    'You do not have permission to edit owner accounts',
  UNAUTHORIZED_TO_DEACTIVATE_OWNER:
    'You do not have permission to deactivate owner accounts',
  UNAUTHORIZED_TO_DELETE_OWNER:
    'You do not have permission to delete owner accounts',
  APPROVED_QUANTITY_MUST_BE_INTEGER: 'Approved quantity must be an integer.',
  ERROR_CLOSING_DIALOG: 'Error closing dialog',
  ERROR_CALCULATING_DIALOG_HEIGHT: 'Error calculating dialog height',
  ERROR_CALCULATING_BOX_HEIGHT: 'Error calculating box height',
  USE_USER_PROFILE_CONTEXT:
    'useUserProfile must be used within a UserProfileProvider',
  ERROR_TRANSFORMING_PURCHASE_ORDER_DATA:
    'Error transforming purchase order data',
  PLEASE_ADD_AT_LEAST_ONE_VALID_ITEM: 'Please add at least one valid item.',
  NO_CHANGES_DETECTED: 'No changes detected.',
  MISSING_ORDER_DATA: 'Missing required order data',
  ORDER_NOT_FOUND: 'Order not found',
  FAILED_TO_LOAD_ORDER: 'Failed to load order details',
  FAILED_TO_UPDATE_ORDER: 'Failed to update order',
  FAILED_TO_FETCH_ORDER_REPORT: 'Failed to fetch order report',
  FAILED_TO_FETCH_PRODUCT_REPORT: 'Failed to fetch product report',
  FAILED_TO_FETCH_CUSTOMER_REPORT: 'Failed to fetch customer report',
  FAILED_TO_FETCH_CUSTOMER_OUTSTANDING_REPORT:
    'Failed to fetch customer outstanding report',
  DATE_RANGE_EXCEEDED: 'Date range cannot exceed 100 days.',
};

export const successMessages = {
  YOU_CANT_DELETE_BY_YOU: 'You cannot Delete your own account',
  SESSION_EXPIRED: 'Session expired!',
  VERIFICATION_UNSUCCESS: 'Error while verifying this user!',
  COMPANY_LOGO_UPDATED: 'Company logo updated',
  PROFILE_PICTURE_UPDATED: 'Profile picture updated',
  GRN_APPROVED_SUCCESSFULLY: 'GRN approved successfully',
  GRN_REJECTED_SUCCESSFULLY: 'GRN rejected successfully',
  GRN_UPDATED_SUCCESSFULLY: 'GRN updated successfully',
  PAYMENT_ADDED_SUCCESSFULLY: 'Payment added successfully',
  PAYMENT_APPROVED_SUCCESSFULLY: 'Payment approved successfully',
  PAYMENT_REJECTED_SUCCESSFULLY: 'Payment rejected successfully',
  PAYMENT_UPDATED_SUCCESSFULLY: 'Payment updated successfully',
  DELIVERY_DETAILS_UPDATED: 'Delivery details updated',
  ORDER_UPDATED_SUCCESSFULLY: 'Order updated successfully',
  ORDER_SAVED_SUCCESSFULLY: 'Order saved successfully',
  CHEQUE_STATUS_UPDATED_SUCCESSFULLY: 'Cheque status updated successfully',
};

export const confirmMessages = {
  CONFIRM_DELETE_USER:
    'Are you sure you want to delete? You will not be able to recover the user afterwards.',
  CONFIRM_ACTIVATE_USER:
    'Activating a user will enable their access to the system.. Are you sure you want to proceed?',
  CONFIRM_DEACTIVATE_USER:
    'Deactivating a user will disable their access to the system.. Are you sure you want to proceed?',
  CONFIRM_DELETE_VENDOR:
    'Are you sure you want to delete? You will not be able to recover the vendor afterwards.',
  CONFIRM_ACTIVATE_VENDOR:
    'Confirm the activation of the selected vendor? The vendor will be available for transactions once again.',
  CONFIRM_DEACTIVATE_VENDOR:
    'Confirm the deactivation of the selected vendor? The vendor will be marked as inactive and will no longer be available for new transactions.',
  CONFIRM_DELETE_MATERIAL:
    'Confirm the deletion of the selected raw material from the inventory.. You will not be able to recover the material afterwards.',
  CONFIRM_ACTIVATE_MATERIAL:
    'Confirm the activation of the selected raw material.. It will be available for use again.',
  CONFIRM_DEACTIVATE_MATERIAL:
    'Confirm the deactivation of the selected raw material.. It will no longer be available for use.',
  CONFIRM_DELETE_PRODUCT:
    'Confirm the deletion of the selected product from the inventory.. This action cannot be undone.',
  CONFIRM_DELETE_VOUCHER_TYPE:
    'Confirm the deletion of the selected voucher type from the system. This action cannot be undone.',
  CONFIRM_ACTIVATE_PRODUCT:
    'Confirm the activation of the selected product.. The product will be available for sale once again.',
  CONFIRM_DEACTIVATE_PRODUCT:
    'Confirm the deactivation of the selected product.. The product will no longer be available for sale.',
  CONFIRM_DELETE_BANK:
    'Confirm the deletion of the selected bank account from the system.. This action cannot be undone.',
  CONFIRM_UPDATE_ACCOUNT_STATUS:
    "Confirm the update of the selected bank account's status. The account status will be updated accordingly.",
  CONFIRM_DELETE_DISCOUNT:
    'Confirm the deletion of the selected discount from the company.. This action cannot be undone.',
  CONFIRM_DELETE_SURCHARGE:
    'Confirm the deletion of the selected surcharge from the company.. This action cannot be undone.',
  CONFIRM_DELETE_CUSTOMER:
    'Confirm the deletion of the selected customer.. This action cannot be undone.',
  CONFIRM_UPDATE_CUSTOMER_STATUS:
    "Confirm the update of the selected customer's status. The account status will be updated accordingly.",
  CONFIRM_DEFAULT_CUSTOMER_ADDRESS:
    'Confirm the setting of this customer address to default.',
  CONFIRM_DELETE_CUSTOMER_ADDRESS:
    'Confirm the deletion of this customer address. This action cannot be undone.',
  CONFIRM_DELETE_PURCHASE_ORDER:
    'Confirm the deletion of the selected purchase order. This action cannot be undone.',
  CONFIRM_DELETE_GRN:
    'Confirm the deletion of the selected GRN. This action cannot be undone.',
  CONFIRM_DELETE_PAYMENT:
    'Confirm the deletion of the selected payment. This action cannot be undone.',
  CONFIRM_APPROVE_GRN:
    'Are you sure you want to approve this GRN? This action will update the GRN status to Approved.',
  CONFIRM_EDIT_GRN: 'Update the details below to edit GRN.',
  CONFIRM_APPROVE_GRN_PAYMENT:
    'Review and confirm the approval of this payment.',
  CONFIRM_REJECT_GRN_PAYMENT: 'Once rejected, this action cannot be reversed.',
};

export const actionButtons = {
  VIEW: 'View',
  EDIT: 'Edit',
  APPROVE: 'Approve',
  REJECT: 'Reject',
  DELETE: 'Delete',
  ACTIVATE: 'Activate',
  DEACTIVATE: 'Deactivate',
  UPDATE: 'Update',
  RESET: 'Reset',
  RESET_PASSWORD: 'Reset Password',
};

export const apiUrls = {
  BASEURL: 'https://api.orderx.online',
  LOGIN: '/login',
  LOGOUT: '/logout',
  SIGNUP: '/signup',
  VERIFY_EMAIL: '/verify-email',
  RESEND_EMAIL: '/resend-email',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD_WITH_TOKEN: '/reset-password-token',
  REFRESH_ACESS_TOKEN: '/refresh-token',

  CHECK_EMAIL_EXISTENCE: '/company/check-email',
  CHECK_REG_NO: '/company/check-registration',
  GET_ENUMS: '/get-enums',
  GET_RAW_MATERIAL_UNITS: '/get-raw-material-units',
  GET_PRODUCT_UNITS: '/get-product-units',
  GET_BANK_NAMES: '/get-bank-names',
  GET_USER_EMAIL: '/user/user-email',
  GET_COMPANY_ROLES: '/user/get-roles',

  USER_MANAGEMENT: '/user/user-management',
  VENDOR_MANAGEMENT: '/vendor/vendor-management',
  VENDOR_LIST_BASIC: '/vendor/basic-active-vendors',
  BASIC_VENDORS: '/vendor/basic-vendors',
  MATERIAL_MANAGEMENT: '/material/raw-material-management',
  MATERIAL_LIST_BASIC: '/material/list-basic',
  PRODUCT_MANAGEMENT: '/product/product-management',
  PRODUCT_LIST_BASIC: '/product/list-basic',
  PRODUCT_UNMAPPED_ACTIVE: '/product/unmapped-active',
  BANK_MANAGEMENT: '/bank_account/bank-acc-management',
  GET_BANK_ACCOUNTS_BASIC_DETAILS: '/bank_account/basic-active-bank-acc',
  BANK_LIST_BASIC: '/bank_account/basic-active-bank-acc',
  VOUCHER_TYPE_MANAGEMENT: '/voucher_type/voucher-type-management',
  VOUCHER_TYPE_LIST_BASIC: '/voucher_type/list-basic',
  DISCOUNT_MANAGEMENT: '/discount/discount-management',
  SURCHARGES_MANAGEMENT: '/surcharges/surcharges-management',
  PRODUCTION_MAPPING: '/production_mapping',
  GRN_MANAGEMENT: '/received_goods/get-grns',
  VOUCHER_MANAGEMENT: '/voucher-management',
  CUSTOMER_MANAGEMENT: '/customer/customer-management',
  ORDER_MANAGEMENT: '/orders/list',
  PRODUCTION_MANAGEMENT: '/production',
  ORDER_REPORT_API: '/reports/order-report',
  PRODUCT_REPORT_API: '/reports/product-report',
  CUSTOMER_REPORT_API: '/reports/customer-report',
  PAYMENT_REPORT_API: '/reports/payment-report',
  CUSTOMER_OUTSTANDING_REPORT_API: '/reports/customer-outstanding-report',
  DASHBOARD_SUMMARY_API: '/reports/dashboard-summary',

  GET_USER_INFO_BY_EMAIL: '/user/user-info',
  ADD_USER: '/user/add-user',
  EDIT_USER: '/user/edit-user',
  DELETE_USER_BY_ID: '/user',
  USER_ACTIVATION: '/user/user-activation',
  ADMIN_RESET_PASSWORD: '/user/admin-reset-password',

  ADD_VENDOR: '/vendor/add-vendor',
  EDIT_VENDOR: '/vendor/edit-vendor',
  DELETE_VENDOR: '/vendor',
  VENDOR_ACTIVATION: '/vendor/activation',

  GET_MATERIAL_INFO_BY_CODE: '/material/raw-material-info',
  GET_MATERIAL_INFO_BY_ID: '/material/raw-material-by-id',

  ADD_RAW_MATERIAL: '/material/add-raw-material',
  EDIT_RAW_MATERIAL: '/material/edit-raw-material',
  DELETE_RAW_MATERIAL: '/material/delete-raw-material',
  RAW_MATERIAL_ACTIVATION: '/material/material-activation',
  RAW_MATERIAL_LOGS: '/raw-material-logs/get-raw-material-logs',
  RESET_RAW_MATERIAL_QUANTITY: '/material/reset-quantity',

  // Raw Material Category endpoints
  RAW_MATERIAL_CATEGORY_LIST: '/raw-material-category/get-categories',
  RAW_MATERIAL_CATEGORY_LIST_BASIC: '/raw-material-category/list-basic',
  RAW_MATERIAL_CATEGORY_BY_ID: '/raw-material-category/get-category',
  ADD_RAW_MATERIAL_CATEGORY: '/raw-material-category/add-category',
  EDIT_RAW_MATERIAL_CATEGORY: '/raw-material-category/edit-category',
  DELETE_RAW_MATERIAL_CATEGORY: '/raw-material-category/delete-category',

  GET_PRODUCT_BY_NAME: '/product/product-info',
  GET_PRODUCT_BY_ID: '/product/product-by-id',
  ADD_PRODUCT: '/product/add-product',
  EDIT_PRODUCT: '/product/edit-product',
  PRODUCT_ACTIVATION: '/product/product-activation',
  DELETE_PRODUCT: '/product/delete-product',
  RESET_PRODUCT_QUANTITY: '/product/reset-quantity',
  PRODUCT_LOGS: '/product-logs/get-product-logs',

  GET_BANK_INFO_BY_ID: '/bank_account/bank-acc-info',
  ADD_BANK: '/bank_account/add-bank-acc',
  EDIT_BANK: '/bank_account/edit-bank-acc',
  BANK_ACTIVATION: '/bank_account/bank-acc-activation',
  DELETE_BANK: '/bank_account/delete-bank-acc',
  GET_VOUCHER_TYPE_BY_ID: '/voucher_type/voucher-type-by-id',
  ADD_VOUCHER_TYPE: '/voucher_type/add-voucher-type',
  EDIT_VOUCHER_TYPE: '/voucher_type/edit-voucher-type',
  DELETE_VOUCHER_TYPE: '/voucher_type/delete-voucher-type',

  GET_DISCOUNT_BY_NAME: '/discount/discount-info',
  GET_DISCOUNT_BY_ID: '/discount/discount-info-by-id',
  ADD_DISCOUNT: '/discount/add-discount',
  EDIT_DISCOUNT: '/discount/edit-discount',
  DELETE_DISCOUNT: '/discount/delete-discount',

  GET_SURCHARGE_BY_NAME: '/surcharges/surcharges-info',
  GET_SURCHARGE_BY_ID: '/surcharges/surcharges-info-by-id',
  ADD_SURCHARGE: '/surcharges/add-surcharges',
  EDIT_SURCHARGE: '/surcharges/edit-surcharges',
  DELETE_SURCHARGE: '/surcharges/delete-surcharges',

  GET_CUSTOMER_BY_ID: '/customer/customer-info',
  ADD_CUSTOMER: '/customer/add-customer',
  EDIT_CUSTOMER: '/customer/edit-customer',
  CUSTOMER_ACTIVATION: '/customer/customer-status',
  DELETE_CUSTOMER: '/customer/delete-customer',

  GET_CUSTOMER_ADDRESSES: '/customer_address/address-management',
  GET_ADDRESS_BY_ID: '/customer_address/address-info',
  ADD_ADDRESS: '/customer_address/add-address',
  EDIT_ADDRESS: '/customer_address/edit-address',
  DELETE_ADDRESS: '/customer_address/delete-address',
  SET_DEFAULT_ADDRESS: '/customer_address/default-address',

  GET_ORDER_BY_ID: '/orders',
  UPDATE_ORDER_DELIVERY: '/orders/delivery-details',
  DELETE_ORDER: '/orders',
  CREATE_ORDER_API: '/orders',
  EDIT_ORDER_API: '/orders',

  // Order Payment endpoints
  GET_ORDER_PAYMENTS: '/orders',
  GET_ORDER_PAYMENT_BY_ID: '/orders/payments',
  ADD_ORDER_PAYMENT: '/orders/payments',
  EDIT_ORDER_PAYMENT: '/orders',
  TOGGLE_ORDER_PAYMENT_STATUS: '/orders/payments',
  DELETE_ORDER_PAYMENT: '/orders/payments',

  GET_PURCHASE_ORDERS_BY_ID: '/purchase_order/purchase-order-info',
  ADD_PURCHASE_ORDERS: '/purchase_order/add-purchase-order',
  EDIT_PURCHASE_ORDERS: '/purchase_order/edit-purchase-order',
  TOGGLE_PURCHASE_ORDER_STATUS: '/purchase_order/purchase-order-status',
  DELETE_PURCHASE_ORDERS: '/purchase_order/delete-purchase-order',
  PURCHASE_ORDER_MANAGEMENT: '/purchase_order/purchase-order-management',

  // VOUCHER endpoints
  GET_VOUCHER: '/vouchers/get-vouchers',
  GET_VOUCHER_BY_ID: '/vouchers/get-voucher',
  ADD_VOUCHER: '/vouchers/add-voucher',
  EDIT_VOUCHER: '/vouchers/edit-voucher',
  VOUCHER_STATUS: '/vouchers/voucher-status',
  DELETE_VOUCHER: '/vouchers/delete-voucher',

  // Voucher Payment endpoints
  GET_VOUCHER_PAYMENTS: '/voucher-payments/get-voucher-payments',
  GET_VOUCHER_PAYMENT_BY_ID: '/voucher-payments/get-voucher-payment',
  ADD_VOUCHER_PAYMENT: '/voucher-payments/add-voucher-payment',
  EDIT_VOUCHER_PAYMENT: '/voucher-payments/edit-voucher-payment',
  TOGGLE_VOUCHER_PAYMENT_STATUS: '/voucher-payments/voucher-payment-status',
  DELETE_VOUCHER_PAYMENT: '/voucher-payments/delete-voucher-payment',

  // GRN endpoints
  GET_GRN_BY_ID: '/received_goods/get-grn',
  ADD_GRN: '/received_goods/add-grn',
  EDIT_GRN: '/received_goods/edit-grn',
  TOGGLE_GRN_STATUS: '/received_goods/grn-status',
  DELETE_GRN: '/received_goods/delete-grn',
  GET_GRN_PAYMENTS: '/received_goods/get-grn-payments',
  GET_GRN_PAYMENT_BY_ID: '/received_goods/get-grn-payment',
  ADD_GRN_PAYMENT: '/received_goods/add-grn-payment',
  EDIT_GRN_PAYMENT: '/received_goods/edit-grn-payment',
  TOGGLE_GRN_PAYMENT_STATUS: '/received_goods/grn-payment-status',
  DELETE_GRN_PAYMENT: '/received_goods/delete-grn-payment',
  UPDATE_CHEQUE_STATUS: '/received_goods/update-cheque-status',

  GET_PROFILE_INFO: '/user/personal-info',
  UPLOAD_AVATAR: '/user/upload-avatar',
  UPDATE_PROFILE_INFO: '/user/update-personal-info',
  RESET_PASSWORD: '/user/reset-password',

  UPLOAD_LOGO: '/company/upload-logo',
  GET_COMPANY_INFO: '/company/company-info',
  GET_COMPANY_BASIC_INFO: '/company/company-basic-info',
  UPDATE_COMPANY_INFO: '/company/update-company-info',
};

export const routeURL = {
  LOGIN: '/login',
  LOGOUT: '/logout',
  SIGN_UP: '/signup',
  VERIFY_EMAIL: '/verify-email',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  USER: '/users',
  DASHBOARD: '/dashboard',
  USER_MANAGEMENT: '/user-management',
  VENDOR_MANAGEMENT: '/vendor-management',
  MATERIAL_MANAGEMENT: '/raw-material-management',
  RAW_MATERIAL_CATEGORY_MANAGEMENT: '/raw-material-category-management',
  PRODUCT_MANAGEMENT: '/product-management',
  BANK_MANAGEMENT: '/bank-management',
  VOUCHER_TYPE_MANAGEMENT: '/voucher-type-management',
  DISCOUNT_MANAGEMENT: '/discount-management',
  SURCHARGES_MANAGEMENT: '/surcharges-management',
  PRODUCTION_MAPPING: '/production-mapping',
  PURCHASE_ORDER_MANAGEMENT: '/purchase-order-management',
  GRN_MANAGEMENT: '/grn-management',
  GRN_PAYMENTS: '/grn-payments/:grnId',
  GRN_PAYMENTS_REDIRECT: (grnId: string) => `/users/grn-payments/${grnId}`,
  VOUCHER_MANAGEMENT: '/voucher-management',
  VOUCHER_PAYMENTS: '/voucher-payments/:voucherId',
  VOUCHER_PAYMENTS_REDIRECT: (voucherId: string) =>
    `/users/voucher-payments/${voucherId}`,
  CUSTOMER_MANAGEMENT: '/customer-management',
  ORDER_MANAGEMENT: '/order-management',
  ORDER_CREATE: '/order-management/create',
  ORDER_EDIT: '/order-management/edit/:orderId',
  ORDER_EDIT_REDIRECT: (orderId: string) =>
    `/users/order-management/edit/${orderId}`,
  ORDER_PAYMENTS: '/order-payments/:orderId',
  ORDER_PAYMENTS_REDIRECT: (orderId: string) =>
    `/users/order-payments/${orderId}`,
  PRODUCTION_MANAGEMENT: '/production-management',
  PRODUCT_LOGS: '/product-logs',
  RAW_MATERIAL_LOGS: '/raw-material-logs',
  CHEQUE_STATUS: '/cheque-status',
  SETTINGS: '/settings',
  ORDER_REPORT: '/order-report',
  PRODUCT_REPORT: '/product-report',
  CUSTOMER_REPORT: '/customer-report',
  PAYMENT_REPORT: '/payment-report',
  CUSTOMER_OUTSTANDING_REPORT: '/customer-outstanding-report',
  UNAUTHORIZED_404: '/404',
};

export const DATE_FORMATS = {
  LOCALE: 'en-GB',
  DISPLAY: 'DD/MM/YYYY',
  API: 'YYYY-MM-DD',
} as const;

export const protectedURL = {
  USER_MANAGEMENT: '/users/user-management',
  VENDOR_MANAGEMENT: '/users/vendor-management',
  MATERIAL_MANAGEMENT: '/users/raw-material-management',
  RAW_MATERIAL_CATEGORY_MANAGEMENT: '/users/raw-material-category-management',
  PRODUCT_MANAGEMENT: '/users/product-management',
  BANK_MANAGEMENT: '/users/bank-acc-management',
  VOUCHER_TYPE_MANAGEMENT: '/users/voucher-type-management',
  DISCOUNT_MANAGEMENT: '/users/discount-management',
  SURCHARGES_MANAGEMENT: '/users/surcharges-management',
  PRODUCTION_MAPPING: '/users/production-mapping',
  PRODUCT_REPORT: '/users/product-report',
  CUSTOMER_REPORT: '/users/customer-report',
  PAYMENT_REPORT: '/users/payment-report',
  CUSTOMER_OUTSTANDING_REPORT: '/users/customer-outstanding-report',
};

export const dataStorageKeywords = {
  ACCESS_TOKEN: 'ACSID',
  REFRESH_TOKEN: 'RFSID',
  IS_LOGGED_IN: 'IGI',
  USER_ID: 'USRID',
  USER_EMAIL: 'USREML',
  COMPANY_ID: 'CMYID',
  FULL_NAME: 'USRFNM',
  ROLES: 'USRRLS',
  HIGHEST_ROLE: 'USRHRL',
  TIMER_REMAINING_TIME: 'RMNTM',
  TIMER_END_TIME: 'TEDTM',
  AVATAR: 'AVT',
};

export const DAY_IN_MILLISECONDS = 1000 * 60 * 60 * 24;

export const RESEND_EXPIRATION_TIME = 240; // 4 minutes in seconds

export const DATE_FORMAT = 'YYYY-MM-DD';

export const pagination = {
  PAGE_LIMIT: 25,
  SEARCH_LIMIT: 50,
  OFFSET: 0,
  COUNT: 0,
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 15,
  DROP_DOWN_LIMIT: 100,
};

export const pageOptions = [25, 50, 100, 250];

export const fileSize = {
  MAX: 2 * 1024 * 1024,
  MAX_MB: 2,
  BYTES_IN_MB: 1024 * 1024,
};

export const BYTES_PER_MB = 1024 * 1024;

export const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];

export const TIME_OUT = 8000;

export const actions = {
  ADD: 'Add',
  UPDATE: 'Update',
  DELETE: 'Delete',
};

export const defaultTimeDuration = '00:00:00';

export const statusType = {
  1: 'Active',
  2: 'Inactive',
};

export const productStatus = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
} as const;

export const customerStatusOptions = [
  { value: CustomerStatus.ACTIVE, label: 'Active' },
  { value: CustomerStatus.INACTIVE, label: 'Inactive' },
  { value: CustomerStatus.SUSPENDED, label: 'Suspended' },
];

export const bankStatusOptions = [
  { value: BankStatus.ACTIVE, label: 'Active' },
  { value: BankStatus.INACTIVE, label: 'Inactive' },
  { value: BankStatus.CLOSED, label: 'Closed' },
];

export const customerActionMenuItems = [
  { action: 'view', label: 'View', icon: 'bx bx-show' },
  { action: 'edit', label: 'Edit', icon: 'bx bx-edit' },
  { action: 'addresses', label: 'Addresses', icon: 'bx bx-map' },
  { action: 'activate', label: 'Activate', icon: 'bx bx-radio-circle-marked' },
  { action: 'deactivate', label: 'Deactivate', icon: 'bx bx-minus-circle' },
  { action: 'delete', label: 'Delete', icon: 'bx bx-trash' },
];

export const customerDialogTitles = {
  VIEW: 'Customer Details',
  EDIT: 'Edit Customer Details',
  ADD_ADDRESS: 'Add Customer Address',
  EDIT_ADDRESS: 'Edit Customer Address',
  DELETE: 'Delete Customer',
  ACTIVATE: 'Activate Customer',
  DEACTIVATE: 'Deactivate Customer',
  DELETE_ADDRESS: 'Delete Customer Address',
  SET_DEFAULT_ADDRESS: 'Set Default Address',
  VIEW_ADDRESSES: 'Address',
  UPDATE_STATUS: 'Update Status',
};

export const customerDialogSubtitles = {
  VIEW: 'View the details of the selected customer.',
  EDIT: 'Update the details below to modify the selected customer information.',
  ADD_ADDRESS: 'Add a new address for the selected customer.',
  EDIT_ADDRESS: 'Update the address details below.',
  DELETE:
    'Confirm the deletion of the selected customer. This action cannot be undone.',
  ACTIVATE:
    'Confirm the activation of the selected customer. The customer will be available for transactions once again.',
  DEACTIVATE:
    'Confirm the deactivation of the selected customer. The customer will no longer be available for transactions.',
  DELETE_ADDRESS:
    'Confirm the deletion of this customer address. This action cannot be undone.',
  SET_DEFAULT_ADDRESS:
    'Confirm the setting of this customer address to default.',
  VIEW_ADDRESSES: "View the customer's address details below.",
  UPDATE_STATUS:
    'Select the appropriate status for the customer from the dropdown below. If suspending, please provide a reason.',
};

export const discountDialogTitles = {
  EDIT: 'Edit Discount',
  DELETE: 'Delete Discount',
};

export const discountDialogSubtitles = {
  EDIT: 'Update the details below to modify discount in the system.',
  DELETE:
    'Confirm the deletion of the selected discount from the company. This action cannot be undone.',
};

export const productDialogTitles = {
  EDIT: 'Edit Product Details',
  DELETE: 'Delete Product',
  ACTIVATE: 'Activate Product',
  DEACTIVATE: 'Deactivate Product',
  RESET: 'Reset Available Quantity',
} as const;

export const productDialogSubtitles = {
  EDIT: 'Update the details below to modify the selected product information.',
  DELETE:
    'This action cannot be undone. Please confirm your decision to permanently remove this product from the system.',
  ACTIVATE:
    'Are you sure you want to activate this product? It will become available for use in the system.',
  DEACTIVATE:
    'Are you sure you want to deactivate this product? It will no longer be available for use in the system.',
  RESET:
    'Enter the new available quantity for this product. This will reset the current stock level.',
} as const;

export const productionMappingDialogTitles = {
  ADD: 'Add New Production Mapping',
  VIEW: 'Production Mapping Details',
  EDIT: 'Edit Production Mapping',
  DELETE: 'Delete Production Mapping',
} as const;

export const productionMappingDialogSubtitles = {
  ADD: 'Select a product and add the raw materials required for production.',
  VIEW: 'View the raw materials and quantities required for this product.',
  EDIT: 'Update the product and raw materials for this production mapping.',
} as const;

export const productionMappingFormTitles = {
  MAPPING_DETAILS: 'Production Mapping Details',
} as const;

export const productionDialogTitles = {
  ADD: 'Add Production',
  VIEW: 'Production Details',
  EDIT: 'Edit Production',
  DELETE: 'Delete Production',
} as const;

export const productionDialogSubtitles = {
  ADD: 'Record a new production entry with product details and quantities.',
  VIEW: 'View the production details and quantities.',
  EDIT: 'Update the production details and quantities.',
} as const;

export const productionFormTitles = {
  PRODUCTION_DETAILS: 'Production Details',
} as const;

export const rawMaterialDialogTitles = {
  EDIT: 'Edit Raw Material Details',
  DELETE: 'Delete Raw Material',
  ACTIVATE: 'Activate Raw Material',
  DEACTIVATE: 'Deactivate Raw Material',
} as const;

export const rawMaterialDialogSubtitles = {
  EDIT: 'Update the details below to modify the selected raw material in the inventory.',
  DELETE:
    'Confirm the deletion of the selected raw material from the inventory.',
  ACTIVATE:
    'Are you sure you want to activate this raw material? It will become available for use in the system.',
  DEACTIVATE:
    'Are you sure you want to deactivate this raw material? It will no longer be available for use in the system.',
} as const;

export const rawMaterialCategoryDialogTitles = {
  ADD: 'Add New Category',
  EDIT: 'Edit Category',
  DELETE: 'Delete Category',
} as const;

export const rawMaterialCategoryDialogSubtitles = {
  ADD: 'Fill in the details below to add a new category to the system.',
  EDIT: 'Update the details below to modify raw material category in the system.',
  DELETE:
    'Confirm the deletion of the selected category from the company This action cannot be undone.',
} as const;

export const vendorDialogTitles = {
  EDIT: 'Edit Vendor Details',
  DELETE: 'Delete Vendor',
  ACTIVATE: 'Activate Vendor',
  DEACTIVATE: 'Deactivate Vendor',
} as const;

export const vendorDialogSubtitles = {
  EDIT: 'Update the details below to modify the selected vendor information.',
  DELETE:
    'Confirm the deletion of the selected vendor from the system. This action cannot be undone.',
  ACTIVATE:
    'Are you sure you want to activate this vendor? They will become available for transactions.',
  DEACTIVATE:
    'Are you sure you want to deactivate this vendor? They will no longer be available for transactions.',
} as const;

export const surchargeDialogTitles = {
  EDIT: 'Edit Surcharge',
  DELETE: 'Delete Surcharge',
} as const;

export const surchargeDialogSubtitles = {
  EDIT: 'Update the details below to modify surcharge in the system.',
  DELETE:
    'This action cannot be undone. Please confirm your decision to permanently remove this surcharge from the system.',
} as const;

export const userDialogTitles = {
  EDIT: 'Edit User Details',
  DELETE: 'Delete User',
  ACTIVATE: 'Activate User',
  DEACTIVATE: 'Deactivate User',
  RESET_PASSWORD: 'Reset Password',
} as const;

export const userDialogSubtitles = {
  EDIT: 'Update the details of this user profile.',
  DELETE:
    'Are you sure you want to delete? You will not be able to recover the user afterwards.',
  ACTIVATE:
    'Activating a user will enable their access to the system.. Are you sure you want to proceed?',
  DEACTIVATE:
    'Deactivating a user will disable their access to the system. Are you sure you want to proceed?',
  RESET_PASSWORD: 'Enter and re-enter a new password for this user below.',
} as const;

export const bankDialogTitles = {
  EDIT: 'Edit Bank Account Details',
  DELETE: 'Delete Bank Account',
  UPDATE_STATUS: 'Update Account Status',
} as const;

export const bankDialogSubtitles = {
  EDIT: 'Update the details below to modify the selected bank account information.',
  DELETE:
    'Confirm the deletion of the selected bank account from the system. This action cannot be undone.',
  UPDATE_STATUS:
    "Confirm the update of the selected bank account's status. The account status will be updated accordingly.",
} as const;

export const actionMenuPopoverPosition = {
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'right' as const,
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'right' as const,
  },
};

export const purchaseOrderStatusOptions = [
  { value: 'New', label: 'New' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
];

export const purchaseOrderActionMenuItems = [
  { action: 'view', label: 'View', icon: 'bx bx-file-find' },
  { action: 'edit', label: 'Edit', icon: 'bx bx-edit' },
  { action: 'update_status', label: 'Update Status', icon: 'bx bx-refresh' },
  { action: 'create_grn', label: 'Create GRN', icon: 'bx bx-add-to-queue' },
  { action: 'delete', label: 'Delete', icon: 'bx bx-trash' },
  { action: 'print', label: 'Print', icon: 'bx bx-printer' },
];

export const defaultPurchaseOrderObject = {
  id: '',
  createdAt: '',
  company: { name: '' },
  vendor: {
    id: '',
    organizationName: '',
    address: '',
    contactPersonName: '',
    contactNo: '',
    email: '',
    isActive: false,
  },
  deliveryAddress: '',
  status: 'New',
  preparedBy: '',
  approvedDate: null,
  notes: '',
  purchaseOrderItems: [],
};

export const orderDialogTitles = {
  APPROVE: 'Approve Order',
  DELETE: 'Delete Order',
} as const;

export const orderDialogSubtitles = {
  APPROVE: 'Confirm the approval of this order to proceed with fulfillment.',
  DELETE:
    'Confirm the deletion of this order. This action is permanent and cannot be undone.',
} as const;

export const voucherDialogTitles = {
  ADD: 'Add New Voucher',
  EDIT: 'Edit Voucher',
  VIEW: 'Voucher Details',
  APPROVE: 'Approve Voucher',
  REJECT: 'Reject Voucher',
  DELETE: 'Delete Voucher',
  PAYMENTS: 'Voucher Payments',
} as const;

export const voucherActionButtons = {
  VIEW: 'View',
  EDIT: 'Edit',
  APPROVE: 'Approve',
  REJECT: 'Reject',
  DELETE: 'Delete',
  UPDATE: 'Update',
  VOUCHER_PAYMENTS: 'Voucher Payments',
  PAYMENTS: 'Payments',
  PRINT: 'Print',
};

export const voucherDialogSubtitles = {
  ADD: 'Fill in the details below to create a new voucher.',
  EDIT: 'Update the details below to modify voucher information.',
  VIEW: 'View the details of the selected voucher.',
  APPROVE: 'Are you sure you want to approve this voucher?',
  APPROVE_SECONDARY: 'Review the voucher details and confirm approval.',
  REJECT: 'Are you sure you want to reject this voucher?',
  REJECT_SECONDARY: 'Once rejected, this action cannot be reversed.',
  DELETE: 'Are you sure you want to delete this voucher?',
  DELETE_SECONDARY: 'This action is permanent and cannot be undone.',
} as const;

export const voucherStatusOptions = [
  { value: 'Pending Approval', label: 'Pending Approval' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Payment Progress', label: 'Payment Progress' },
  { value: 'Payment Completed', label: 'Payment Completed' },
];

export const purchaseOrderDialogTitles = {
  EDIT: 'Edit Purchase Order',
  VIEW: 'Purchase Order Details',
  UPDATE_STATUS: 'Update Status',
};

export const purchaseOrderDialogSubtitles = {
  EDIT: 'Fill in the details below to create a new purchase order.',
  VIEW: 'Items',
  UPDATE_STATUS: 'Items',
};

export const purchaseOrderFormTitles = {
  ORDER_DETAILS: 'Order Details',
  ITEM_DETAILS: 'Item Details',
  ITEMS: 'Items',
};

// GRN Properties
export const grnStatusOptions = [
  { value: 'New', label: 'New' },
  { value: 'Pending Approval', label: 'Pending Approval' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Payment Progress', label: 'Payment Progress' },
  { value: 'Paid', label: 'Paid' },
];

export const grnActionMenuItems = [
  { action: 'view', label: 'View', icon: 'bx bx-file-find' },
  { action: 'edit', label: 'Edit', icon: 'bx bx-edit' },
  { action: 'update_status', label: 'Update Status', icon: 'bx bx-refresh' },
  { action: 'payments', label: 'Payments', icon: 'bx bx-money' },
  { action: 'delete', label: 'Delete', icon: 'bx bx-trash' },
  { action: 'print', label: 'Print', icon: 'bx bx-printer' },
];

// ####### https://github.com/techorin/orderX-frontend/issues/159
export const grnDialogTitles = {
  ADD: 'Add New GRN',
  EDIT: 'Edit GRN Details',
  VIEW: 'GRN Details',
  UPDATE_STATUS: 'Update GRN Status',
  PAYMENTS: 'GRN Payments',
  DELETE: 'Delete GRN',
  APPROVE: 'Approve GRN',
  FORM_TITLE: 'GRN Details',
};

export const grnPaymentDialogTitles = {
  ADD: 'Add GRN Payment',
  EDIT: 'Edit GRN Payment',
  VIEW: 'GRN Payment Details',
  APPROVE: 'Approve Payment',
  DELETE: 'Delete Payment',
  REJECT: 'Reject Payment',
} as const;

export const grnPaymentDialogSubtitles = {
  ADD: 'Fill in the details below to add a new GRN payment.',
  EDIT: 'Update the details below to edit GRN payment.',
  APPROVE: 'Are you sure you want to approve this payment?',
  DELETE:
    'Are you sure you want to delete this payment? This action cannot be undone.',
  REJECT: 'Are you sure you want to reject this payment?',
} as const;

export const orderPaymentDialogTitles = {
  ADD: 'Add Order Payment',
  EDIT: 'Edit Order Payment',
  VIEW: 'Order Payment Details',
  APPROVE: 'Approve Payment',
  DELETE: 'Delete Payment',
} as const;

export const orderPaymentDialogSubtitles = {
  ADD: 'Fill in the details below to add a new order payment.',
  EDIT: 'Update the details below to edit order payment.',
  APPROVE: 'Are you sure you want to approve this payment?',
  DELETE:
    'Are you sure you want to delete this payment? This action cannot be undone.',
} as const;

export const grnDialogSubtitles = {
  ADD: 'Fill in the details below to create a new GRN.',
  EDIT: 'Update the details below to modify the GRN.',
  VIEW: 'View the details of the selected GRN.',
  UPDATE_STATUS: 'Update the status of the GRN.',
  PAYMENTS: 'Manage payments for this GRN.',
  DELETE:
    'Confirm the deletion of the selected GRN. This action cannot be undone.',
};

export const grnFormTitles = {
  GRN_DETAILS: 'GRN Details',
  ITEM_DETAILS: 'Item Details',
  ITEMS: 'Items',
  PAYMENT_DETAILS: 'Payment Details',
};

export const companySettings = {
  BASE_BOX_HEIGHT: 850,
  ERROR_HEIGHT_INCREMENT: 30,
  DEFAULT_LOGO: '/default-logo.png',
  LOGO_PATH_PREFIX: '/assets/company-logo',
} as const;

export const profileSettings = {
  BASE_BOX_HEIGHT: 415,
  ERROR_HEIGHT_INCREMENT: 30,
  DEFAULT_AVATAR: '/default-avatar.png',
  AVATAR_PATH_PREFIX: '/assets/user-profile',
  DEFAULT_COUNTRY_CODE: '94',
} as const;

export const emptyStateMessages = {
  NO_DATA_TITLE: 'No Data Available',
  NO_DATA_SUBTITLE:
    'Currently, there is no data available for display. Please add new data to update this section.',
} as const;

export const dialogTitles = {
  ADD_RAW_MATERIAL: 'Add New Raw Material',
  ADD_USER: 'Add New User',
  ADD_VENDOR: 'Add New Vendor',
  ADD_VOUCHER_TYPE: 'Add New Voucher Type',
} as const;

export const dialogSubtitles = {
  ADD_RAW_MATERIAL:
    'Fill in the details below to add a new raw material to the inventory.',
  ADD_USER: 'Fill in the details below to create a new user profile.',
  ADD_VENDOR: 'Fill in the details below to register a new vendor.',
  ADD_VOUCHER_TYPE:
    'Fill in the details below to add a new voucher to the system.',
} as const;

export const verificationSettings = {
  TITLE: 'Verify Your Email',
  SUBTITLE: 'Check your email & click the link to verify your account.',
} as const;

export const verificationCompareMessages = {
  ALREADY_VERIFIED: 'already verified',
};

export const signupSettings = {
  CREATE_ACCOUNT_TITLE: 'Create Your Account',
  COMPANY_EMAIL_SUBTITLE:
    'Please enter the company email to create your account',
  COMPANY_INFO_SUBTITLE: 'Company Information',
  PROFILE_INFO_SUBTITLE: 'Profile Information',
} as const;

export const userBasePath = '/users';

export const selectStatusPlaceholder = 'Select Status';

export const dialogTypeValues = {
  CUSTOMER: 'customer',
  PURCHASE: 'purchase',
  STATUS: 'status',
} as const;

export const dialogButtonLabels = {
  SAVE: 'Save',
} as const;

export const approvalLabels = {
  APPROVED_BY: 'Approved By',
  REJECTED_BY: 'Rejected By',
  APPROVED_DATE: 'Approved Date',
  REJECTED_DATE: 'Rejected Date',
} as const;

export const dialogSpacingValues = {
  MARGIN_LEFT_PURCHASE: 8,
  MARGIN_LEFT_STATUS: 5,
  MARGIN_TOP_BUTTON: 3,
  MARGIN_BOTTOM_BUTTON: 2,
  STATUS_CONTAINER_WIDTH: 594,
  CIRCULAR_PROGRESS_SIZE: 24,
  PURCHASE_SECTION_MARGIN_LEFT: 8,
  PURCHASE_SECTION_MARGIN_TOP: 3,
  PURCHASE_SECTION_MARGIN_BOTTOM: 2,
  STATUS_SECTION_MARGIN_TOP: 3,
  STATUS_SECTION_MARGIN_BOTTOM: 1,
  STATUS_SECTION_CONTENT_MARGIN_LEFT: 3,
  STATUS_SECTION_CONTENT_MARGIN_TOP: 1,
  STATUS_FLEX_GAP: 2,
  STATUS_TITLE_MARGIN_BOTTOM: 0.5,
  STATUS_NEW_PENDING_MARGIN_TOP: 2,
} as const;

export const pageTitles = {
  GRN_PAYMENTS: 'Payments',
  ORDER_PAYMENTS: 'Order Payments',
  VOUCHER_PAYMENTS: 'Payments',
} as const;

export const voucherPaymentDialogTitles = {
  ADD: 'Add New Payment',
  EDIT: 'Edit Payment',
  VIEW: 'Voucher Payment Details',
  APPROVE: 'Approve Payment',
  REJECT: 'Reject Payment',
  DELETE: 'Delete Payment',
} as const;

export const voucherPaymentDialogSubtitles = {
  ADD: 'Fill in the details below to add a new payment.',
  EDIT: 'Update the details below to edit payment.',
  APPROVE: 'Are you sure you want to approve this payment?',
  APPROVE_SECONDARY: 'Review the payment details and confirm approval.',
  REJECT: 'Are you sure you want to reject this payment?',
  REJECT_SECONDARY: 'Once rejected, this action cannot be reversed.',
  DELETE: 'Are you sure you want to delete this payment?',
  DELETE_SECONDARY: 'This action is permanent and cannot be undone.',
} as const;

export const voucherPaymentActionButtons = {
  VIEW: 'View',
  EDIT: 'Edit',
  APPROVE: 'Approve',
  REJECT: 'Reject',
  DELETE: 'Delete',
  UPDATE: 'Update',
  VOUCHER_PAYMENTS: 'Voucher Payments',
  PAYMENTS: 'Payments',
};
